/**
 * One-shot, in-memory request from the Settings notes list ("Notes you
 * started", testimony plan rev 4 section 9.2 and the Step 3 proposal) to open
 * a prayer's testimony dialog on the home page. Presentation only: nothing
 * here reads or writes content, and nothing is kept in history state or
 * storage, so a reload or a later Back can never replay it.
 *
 * Lifecycle: the list sets a request and navigates home. PrayPage claims it
 * for its own history entry (`routeKey`, so React StrictMode's simulated
 * remount on the same entry continues the same claim, while a later visit on a
 * different entry finds it bound elsewhere), validates it after its reads, and
 * consumes it exactly once whether it opens or drops it. Any new request, a
 * Settings mount, or age past the limit ends an older one.
 */
export interface TestimonyOpenRequest {
  id: string;
  ownerId: string;
  authEpoch: number;
  prayerId: string;
  draftId: string;
  requestedAt: number;
  routeKey: string | null;
  /** The PrayPage effect run working on it (StrictMode re-runs take over). */
  runToken: number | null;
}

/** Longest time between the tap and the home page opening the dialog. */
export const TESTIMONY_OPEN_MAX_AGE_MS = 10_000;

let pending: TestimonyOpenRequest | null = null;
let returnFocus: { ownerId: string; authEpoch: number; draftId: string } | null = null;
let nextId = 0;

export function requestTestimonyOpen(input: {
  ownerId: string;
  authEpoch: number;
  prayerId: string;
  draftId: string;
}): TestimonyOpenRequest {
  nextId += 1;
  pending = {
    id: `testimony-open-${nextId}-${Date.now()}`,
    ...input,
    requestedAt: Date.now(),
    routeKey: null,
    runToken: null,
  };
  returnFocus = { ownerId: input.ownerId, authEpoch: input.authEpoch, draftId: input.draftId };
  return { ...pending };
}

/**
 * Bind the pending request to this history entry and effect run, or return
 * null. A request bound to a different entry means the home page has moved
 * on since the tap (a layer opened, Back, any other navigation), so it is
 * ended; a request past the age limit is ended too.
 */
export function claimTestimonyOpenRequest(
  routeKey: string,
  runToken: number,
  now = Date.now(),
): TestimonyOpenRequest | null {
  if (!pending) return null;
  if (now - pending.requestedAt > TESTIMONY_OPEN_MAX_AGE_MS || now < pending.requestedAt) {
    pending = null;
    return null;
  }
  if (pending.routeKey !== null && pending.routeKey !== routeKey) {
    pending = null;
    return null;
  }
  pending = { ...pending, routeKey, runToken };
  return { ...pending };
}

/**
 * The effect run that claimed it has ended. When no newer run of the same
 * page took the request over (as StrictMode's immediate re-run does), the
 * page unmounted or moved on: end it so it can never open later.
 */
export function abandonTestimonyOpenRequest(id: string, runToken: number): void {
  if (pending && pending.id === id && pending.runToken === runToken) pending = null;
}

/** True while exactly this request is still pending (not consumed or replaced). */
export function testimonyOpenRequestPending(id: string, now = Date.now()): boolean {
  return pending !== null && pending.id === id &&
    now - pending.requestedAt <= TESTIMONY_OPEN_MAX_AGE_MS && now >= pending.requestedAt;
}

/** End exactly this request. Returns false when it was already gone. */
export function consumeTestimonyOpenRequest(id: string): boolean {
  if (!pending || pending.id !== id) return false;
  pending = null;
  return true;
}

/** End any pending request (a Settings mount, an account change). */
export function cancelTestimonyOpenRequest(): void {
  pending = null;
}

/**
 * The draft whose Open was tapped last, for focus when the list mounts again.
 * Returned only to the same owner and auth epoch, and only once.
 */
export function takeTestimonyReturnFocus(ownerId: string, authEpoch: number): string | null {
  const focus = returnFocus;
  returnFocus = null;
  if (!focus || focus.ownerId !== ownerId || focus.authEpoch !== authEpoch) return null;
  return focus.draftId;
}

/** Probe and test support: the current state, copied. */
export function peekTestimonyOpenState(): {
  pending: TestimonyOpenRequest | null;
  returnFocus: { ownerId: string; authEpoch: number; draftId: string } | null;
} {
  return {
    pending: pending ? { ...pending } : null,
    returnFocus: returnFocus ? { ...returnFocus } : null,
  };
}
